import React, { useMemo, useState, useCallback } from "react";
import axios from "axios";
import { useStore } from "react-redux";
import { Form } from "react-bootstrap";

const Auftragsobergrenze = () => {
  const { configStorage, rightStorage, mandantStorage } = useStore().getState();
  const readOnly = !rightStorage.isAdmin();

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [dirtyFields, setDirtyFields] = useState({});

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const requestData = configStorage.getRequestData("mandantOrderLimit");
      if (!requestData || !requestData.url) {
        setLoading(false);
        return;
      }

      const { request, json } = requestData;
      let url = requestData.url;

      if (configStorage.isBLBAdmin() && !mandantStorage.isBLBAdmin() && mandantStorage.gpNummer) {
        url = url.replace(/\/$/, "") + "/" + Number(mandantStorage.gpNummer);
      }

      const { type, config } = request || {};
      const axiosInstance = axios.create(config);
      const response = await axiosInstance[type || "get"](url, json);

      if (response.data && response.data.mandantOrderLimit) {
        setData(response.data.mandantOrderLimit);
        setIsDirty(false);
        setDirtyFields({});
      } else {
        setError("Keine Obergrenzen-Daten vorhanden.");
      }
    } catch (e) {
      setError("Fehler beim Laden der Obergrenze.");
    } finally {
      setLoading(false);
    }
  }, [configStorage, mandantStorage]);

  useMemo(() => {
    loadData();
  }, [loadData]);

  const [validationErrors, setValidationErrors] = useState({});

  const validateField = (field, numericValue) => {
    let error = null;

    if (numericValue !== null) {
      if (numericValue < 0) {
        error = "Wert darf nicht kleiner als 0 sein";
      }

      if (field === "ifSell" && numericValue > 250000) {
        error = "Der Wert darf maximal 250000 sein";
      }
    }

    setValidationErrors(prev => ({
      ...prev,
      [field]: error
    }));

    return error;
  };

  const handleChange = (field, value) => {
    const numericValue = value === "" ? null : Number(value);

    validateField(field, numericValue);

    setData(prev => ({
      ...prev,
      [field]: numericValue
    }));

    setIsDirty(true);
    setDirtyFields(prev => ({
      ...prev,
      [field]: true
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // Prevent save if validation errors exist
      const hasErrors = Object.values(validationErrors).some(e => !!e);
      if (hasErrors) {
        setError("Bitte korrigieren Sie die ungültigen Werte.");
        return;
      }

      const requestData = configStorage.getRequestData(
        "mandantOrderLimit",
        "post",
        { mandantOrderLimit: data }
      );

      const { request, json } = requestData;
      const { type, config } = request || {};
      const axiosInstance = axios.create(config);
      let url = requestData.url;

      if (configStorage.isBLBAdmin() && !mandantStorage.isBLBAdmin() && mandantStorage.gpNummer) {
        url = url.replace(/\/$/, "") + "/" + Number(mandantStorage.gpNummer);
      }

      const response = await axiosInstance[type || "post"](
        url,
        json
      );

      if (response.data) {
        await loadData(); // reload fresh data from GET endpoint
      }
    } catch (e) {
      setError("Fehler beim Speichern der Obergrenze.");
    } finally {
      setSaving(false);
    }
  };

  const rows = [
    {
      label: "Berater 1",
      sellKey: "berater1Sell",
      buyKey: "berater1Buy"
    },
    {
      label: "Berater 2",
      sellKey: "berater2Sell",
      buyKey: "berater2Buy"
    },
    {
      label: "Call-Center",
      sellKey: "callCenterSell",
      buyKey: "callCenterBuy"
    },
    {
      label: "IF",
      sellKey: "ifSell",
      buyKey: null
    }
  ];

  if (loading) {
    return <div>Laden...</div>;
  }

  const lastUpdate =
    data?.updateTs
      ? new Date(data.updateTs).toLocaleDateString("de-DE") +
        " " +
        new Date(data.updateTs).toLocaleTimeString("de-DE").substring(0, 5)
      : null;

  return (
    <div>
      <h3>Auftragsobergrenze</h3>
      <div className="d-flex w-100 fs-6 justify-content-between mt-2">
        Hier kann der Maximalbetrag für einen Auftrag je Berechtigungsstufe erfasst werden. Bitte beachten Sie, dass beim Verkauf unabhängig vom hinterlegten Wert nur Bestellungen bis zur jeweiligen Versicherungsgrenze erfasst werden können.
      </div>
        
      {error && (
        <div className="alert alert-danger fs-6 p-1">
          {error}
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-bordered">
          <thead>
            <tr>
              <th></th>
              <th>Verkauf</th>
              <th>Ankauf</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                <td>{row.label}</td>

                <td>
                  <Form.Control
                    type="number"
                    min="0"
                    max={row.sellKey === "ifSell" ? 250000 : undefined}
                    value={data?.[row.sellKey] ?? ""}
                    className={dirtyFields[row.sellKey] ? "bg-warning" : ""}
                    isInvalid={!!validationErrors[row.sellKey]}
                    disabled={readOnly}
                    onChange={(e) =>
                      handleChange(row.sellKey, e.target.value)
                    }
                  />
                  {validationErrors[row.sellKey] && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors[row.sellKey]}
                    </Form.Control.Feedback>
                  )}
                </td>

                <td>
                  {row.buyKey ? (
                    <>
                      <Form.Control
                        type="number"
                        min="0"
                        value={data?.[row.buyKey] ?? ""}
                        className={dirtyFields[row.buyKey] ? "bg-warning" : ""}
                        isInvalid={!!validationErrors[row.buyKey]}
                        disabled={readOnly}
                        onChange={(e) =>
                          handleChange(row.buyKey, e.target.value)
                        }
                      />
                      {validationErrors[row.buyKey] && (
                        <Form.Control.Feedback type="invalid">
                          {validationErrors[row.buyKey]}
                        </Form.Control.Feedback>
                      )}
                    </>
                  ) : (
                    ""
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(lastUpdate || data?.modifiedBy) && (
        <div className="d-flex w-100 fs-6 justify-content-between mt-2">
          <div>
            {data?.modifiedBy ? `Geändert von: ${data.modifiedBy}` : ""}
          </div>
          <div>
            {lastUpdate ? `Letzte Änderung: ${lastUpdate}` : ""}
          </div>
        </div>
      )}

      <div className="d-flex justify-content-start align-items-center mt-3">
        {!readOnly
          ? <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={
              saving ||
              Object.values(validationErrors).some(e => !!e)
            }
          >
            {saving ? "Speichern..." : "Übernehmen"}
          </button>
          : null
        }

        {!readOnly && isDirty && (
          <span className="text-danger ms-3">
            Daten sind noch nicht gespeichert. Bitte speichern Sie die Daten mit dem „Übernehmen“-Button
          </span>
        )}
      </div>
    </div>
  );
};

export default Auftragsobergrenze;
